import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PERMISSIONS, PERMISSION_INFO, PERMISSION_PRESETS, ROLE_DEFAULTS, roleRank, type Permission } from "@astroplanet/shared";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { type StaffMember, type StaffRoleName, type StaffView, useRefreshStaff, useStaff } from "../../hooks/staff";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { Switch } from "../catalog/fields";

const ROLES: StaffRoleName[] = ["cashier", "manager", "admin", "owner"];
type PresetKey = keyof typeof PERMISSION_PRESETS | "role" | "custom";
const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

interface Form {
  name: string; username: string; pin: string; role: StaffRoleName; locale: "ar" | "en"; active: boolean;
  permissions: Permission[] | null; branchIds: string[]; canApprove: boolean; managerPin: string; clearManagerPin: boolean;
}

function StaffDialog({ member, data, onClose }: { member: StaffMember | null; data: StaffView; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const { user } = useAuth();
  const refresh = useRefreshStaff();
  const mineRank = roleRank(data.me.role);
  const [f, setF] = useState<Form>(
    member
      ? { name: member.name, username: member.username, pin: "", role: member.role, locale: member.locale, active: member.active, permissions: member.permissions as Permission[] | null, branchIds: member.branches.map((b) => b.branchId), canApprove: member.branches.some((b) => b.canApproveOverrides), managerPin: "", clearManagerPin: false }
      : { name: "", username: "", pin: "", role: "cashier", locale: "ar", active: true, permissions: [...PERMISSION_PRESETS.pos_only], branchIds: data.branches.slice(0, 1).map((b) => b.id), canApprove: false, managerPin: "", clearManagerPin: false },
  );
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const [busy, setBusy] = useState(false);
  const full = f.role === "admin" || f.role === "owner";
  const effective: readonly Permission[] = full ? PERMISSIONS : (f.permissions ?? ROLE_DEFAULTS[f.role]);
  const preset: PresetKey = f.permissions === null ? "role" : ((Object.entries(PERMISSION_PRESETS).find(([, v]) => sameSet(v, f.permissions!))?.[0] as PresetKey | undefined) ?? "custom");
  const isSelf = member?.id === user?.id;
  const restricted = isSelf && !full; // editing yourself: role / status / permissions are locked

  const toggle = (p: Permission, on: boolean) => setF((x) => {
    const base = x.permissions ?? [...ROLE_DEFAULTS[x.role]];
    return { ...x, permissions: PERMISSIONS.filter((q) => (q === p ? on : base.includes(q))) };
  });
  const branchesBody = f.branchIds.map((branchId) => ({ branchId, canApproveOverrides: f.canApprove }));
  const valid = f.name.trim().length >= 2 && (member ? true : /^[a-zA-Z0-9._-]{2,32}$/.test(f.username) && /^\d{4,8}$/.test(f.pin)) && (full || f.branchIds.length > 0) && (!f.managerPin || /^\d{4,8}$/.test(f.managerPin));

  async function submit() {
    setBusy(true);
    try {
      if (member) {
        await api.patch(`/staff/${member.id}`, {
          name: f.name.trim(), locale: f.locale,
          ...(restricted ? {} : { role: f.role, active: f.active, permissions: full ? null : f.permissions }),
          branches: branchesBody,
          ...(f.managerPin ? { managerPin: f.managerPin } : f.clearManagerPin ? { managerPin: null } : {}),
        });
      } else {
        await api.post("/staff", {
          name: f.name.trim(), username: f.username.trim(), pin: f.pin, role: f.role, locale: f.locale, branches: branchesBody, permissions: full ? null : f.permissions,
          ...(f.managerPin ? { managerPin: f.managerPin } : {}),
        });
      }
      await refresh();
      toast(t("staff.saved"), "success");
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  const groups = (["daily", "business", "control"] as const).map((g) => ({ g, items: PERMISSIONS.filter((p) => PERMISSION_INFO[p].group === g) }));
  return (
    <Modal open size="lg" onClose={onClose} title={member ? t("staff.editTitle", { name: member.name }) : t("staff.newTitle")}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!valid || busy} onClick={() => void submit()}>{t("staff.save")}</button></>}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div><label className="label" htmlFor="sf-name">{t("staff.name")}</label><input id="sf-name" className="field" value={f.name} maxLength={80} onChange={(e) => set("name", e.target.value)} /></div>
        <div><label className="label" htmlFor="sf-user">{t("staff.username")}</label><input id="sf-user" className="field num font-mono" value={f.username} disabled={!!member} maxLength={32} autoComplete="off" onChange={(e) => set("username", e.target.value.toLowerCase())} /></div>
        {!member && <div><label className="label" htmlFor="sf-pin">{t("staff.pin")}</label><input id="sf-pin" className="field num font-mono" type="password" inputMode="numeric" autoComplete="new-password" maxLength={8} value={f.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, ""))} /><p className="mt-0.5 text-[10px] font-semibold text-gray-500">{t("staff.pinHelp")}</p></div>}
        <div>
          <label className="label" htmlFor="sf-role">{t("staff.role")}</label>
          <select id="sf-role" className="field" value={f.role} disabled={restricted} onChange={(e) => set("role", e.target.value as StaffRoleName)}>
            {ROLES.filter((r) => roleRank(r) <= mineRank && (r !== "owner" || data.me.role === "owner")).map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="sf-lang">{t("staff.language")}</label>
          <select id="sf-lang" className="field" value={f.locale} onChange={(e) => set("locale", e.target.value as "ar" | "en")}><option value="ar">العربية</option><option value="en">English</option></select>
        </div>
        {member && <div className="self-end"><Switch checked={f.active} disabled={restricted} onChange={(v) => set("active", v)}>{t("staff.active")}</Switch></div>}
      </div>

      <fieldset className="space-y-2 rounded-2xl border border-gray-200 p-3">
        <legend className="px-1 text-[11px] font-black text-gray-600">{t("staff.branches")}</legend>
        {full && <p className="text-[11px] font-semibold text-gray-500">{t("staff.allBranches")}</p>}
        <div className="flex flex-wrap gap-2">
          {data.branches.map((b) => (
            <label key={b.id} className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-xs font-bold ${f.branchIds.includes(b.id) ? "border-cosmic-purple bg-purple-50" : "border-gray-200"}`}>
              <input type="checkbox" className="rounded text-cosmic-purple focus:ring-0" checked={f.branchIds.includes(b.id)} onChange={(e) => set("branchIds", e.target.checked ? [...f.branchIds, b.id] : f.branchIds.filter((x) => x !== b.id))} />
              {(locale === "ar" && b.name_ar) || b.name} <span className="num font-mono text-[10px] text-gray-400">{b.code}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-2xl border border-purple-200 bg-purple-50/30 p-3" disabled={restricted}>
        <legend className="px-1 text-[11px] font-black text-cosmic-purple">🔐 {t("staff.access")}</legend>
        {full ? (
          <p className="text-xs font-bold text-emerald-700">✓ {t("staff.fullAccess")}</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t("staff.preset.label")}>
              {(["pos_only", "front_desk", "supervisor", "back_office", "role"] as const).map((k) => (
                <button key={k} type="button" role="radio" aria-checked={preset === k} data-preset={k}
                  onClick={() => set("permissions", k === "role" ? null : [...PERMISSION_PRESETS[k]])}
                  className={`rounded-xl border-2 px-3 py-1.5 text-[11px] font-black ${preset === k ? "border-cosmic-purple bg-white text-cosmic-purple" : "border-gray-200 bg-white text-gray-600 hover:border-cosmic-purple"}`}>
                  {k === "role" ? t("staff.preset.role", { role: t(`roles.${f.role}`) }) : t(`staff.preset.${k}`)}
                </button>
              ))}
              {preset === "custom" && <span className="rounded-xl bg-amber-100 px-3 py-1.5 text-[11px] font-black text-amber-900">{t("staff.preset.custom")}</span>}
            </div>
            <p className="text-[11px] font-semibold text-gray-600">{t(`staff.presetHelp.${preset}`)}</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {groups.map(({ g, items }) => (
                <div key={g} className="space-y-1.5 rounded-xl border border-gray-200 bg-white p-2.5">
                  <div className="text-[10px] font-black uppercase tracking-wide text-gray-500">{t(`staff.group.${g}`)}</div>
                  {items.map((p) => (
                    <label key={p} className="flex items-start gap-2 text-xs font-bold text-gray-700">
                      <input type="checkbox" className="mt-0.5 rounded text-cosmic-purple focus:ring-0" checked={effective.includes(p)} aria-label={t(`staff.perm.${p}.title`)} onChange={(e) => toggle(p, e.target.checked)} />
                      <span>
                        {t(`staff.perm.${p}.title`)} {PERMISSION_INFO[p].sensitive && <span title={t("staff.sensitive")}>⚠️</span>}
                        <span className="block text-[10px] font-semibold text-gray-500">{t(`staff.perm.${p}.desc`)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </fieldset>

      {f.role !== "cashier" ? (
        <fieldset className="space-y-2 rounded-2xl border border-gray-200 p-3">
          <legend className="px-1 text-[11px] font-black text-gray-600">{t("staff.override.title")}</legend>
          <Switch checked={f.canApprove} onChange={(v) => set("canApprove", v)}>{t("staff.override.can")}</Switch>
          <div className="grid max-w-sm grid-cols-1 gap-2">
            <label className="label" htmlFor="sf-mpin">{member?.hasManagerPin ? t("staff.override.pinChange") : t("staff.override.pin")}</label>
            <input id="sf-mpin" className="field num font-mono" type="password" inputMode="numeric" autoComplete="new-password" maxLength={8} value={f.managerPin} onChange={(e) => set("managerPin", e.target.value.replace(/\D/g, ""))} />
            {member?.hasManagerPin && <Switch checked={f.clearManagerPin} onChange={(v) => set("clearManagerPin", v)}>{t("staff.override.clear")}</Switch>}
          </div>
        </fieldset>
      ) : null}
    </Modal>
  );
}

function ResetPin({ member, onClose }: { member: StaffMember; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    try {
      await api.post(`/staff/${member.id}/reset-pin`, { pin });
      toast(t("staff.pinReset"), "success");
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open size="sm" onClose={onClose} title={t("staff.resetPinTitle", { name: member.name })}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!/^\d{4,8}$/.test(pin) || busy} onClick={() => void submit()}>{t("staff.resetPin")}</button></>}>
      <label className="label" htmlFor="rp-pin">{t("staff.newPin")}</label>
      <input id="rp-pin" className="field num font-mono" type="password" inputMode="numeric" autoComplete="new-password" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
      <p className="text-[11px] font-semibold text-gray-500">{t("staff.pinHelp")}</p>
    </Modal>
  );
}

/** Staff & permissions: add and remove people, choose what each one may see and do. */
export function StaffPanel() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const { user } = useAuth();
  const { data, isLoading, isError } = useStaff();
  const refresh = useRefreshStaff();
  const [edit, setEdit] = useState<{ member: StaffMember | null } | null>(null);
  const [pinFor, setPinFor] = useState<StaffMember | null>(null);
  const [showOff, setShowOff] = useState(false);
  const branchName = useMemo(() => new Map((data?.branches ?? []).map((b) => [b.id, (locale === "ar" && b.name_ar) || b.name])), [data, locale]);
  const mineRank = data ? roleRank(data.me.role) : 0;

  async function toggleActive(m: StaffMember) {
    try {
      await api.patch(`/staff/${m.id}`, { active: !m.active });
      await refresh();
      toast(t(m.active ? "staff.disabled" : "staff.enabled", { name: m.name }), "success");
    } catch (e) {
      toastError(e);
    }
  }
  async function remove(m: StaffMember) {
    if (!window.confirm(t("staff.confirmDelete", { name: m.name }))) return;
    try {
      const r = await api.delete<{ deactivatedOnly: boolean }>(`/staff/${m.id}`);
      await refresh();
      toast(r.deactivatedOnly ? t("staff.deactivatedInstead", { name: m.name }) : t("staff.deleted", { name: m.name }), "success");
    } catch (e) {
      toastError(e);
    }
  }

  const list = (data?.staff ?? []).filter((s) => showOff || s.active);
  const summary = (m: StaffMember) => (m.role === "admin" || m.role === "owner" ? t("staff.fullAccess") : m.effective.length === 1 && m.effective[0] === "pos" ? t("staff.posOnly") : t("staff.pagesCount", { count: m.effective.length }));

  return (
    <div className="space-y-5">
      <div className="card flex items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>🔐</div>
        <div>
          <h1 className="text-xl font-black tracking-tight">{t("staff.title")}</h1>
          <p className="text-xs font-medium text-gray-500">{t("staff.subtitle")}</p>
        </div>
      </div>
      <section className="card space-y-3 p-4 sm:p-5" aria-label={t("staff.title")}>
        <SectionHeader
          title={t("staff.list")}
          right={<div className="flex items-center gap-3"><Switch checked={showOff} onChange={setShowOff}>{t("staff.showDisabled")}</Switch><button type="button" className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setEdit({ member: null })}>＋ {t("staff.add")}</button></div>}
        />
        {isLoading ? <Empty>{t("common.loading")}</Empty> : isError || !data ? <Empty>{t("staff.loadError")}</Empty> : list.length === 0 ? <Empty>{t("staff.none")}</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-xs">
              <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
                <tr><th className="px-3 py-2 text-start">{t("staff.name")}</th><th className="px-2 py-2 text-start">{t("staff.role")}</th><th className="px-2 py-2 text-start">{t("staff.access")}</th><th className="px-2 py-2 text-start">{t("staff.branches")}</th><th className="px-2 py-2 text-center">{t("staff.status")}</th><th className="px-3 py-2" /></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
                {list.map((m) => {
                  const mine = m.id === user?.id;
                  const locked = roleRank(m.role) > mineRank;
                  return (
                    <tr key={m.id} data-user={m.username} className={m.active ? "" : "opacity-60"}>
                      <td className="px-3 py-2.5"><div className="font-extrabold text-cosmic-navy">{m.name}{mine && <span className="ms-1 text-[10px] font-black text-cosmic-purple">({t("staff.you")})</span>}</div><div className="num font-mono text-[10px] text-gray-400">{m.username}</div></td>
                      <td className="px-2 py-2.5"><Pill tone={m.role === "owner" ? "purple" : m.role === "admin" ? "amber" : m.role === "manager" ? "green" : "gray"}>{t(`roles.${m.role}`)}</Pill></td>
                      <td className="px-2 py-2.5">
                        <div>{summary(m)}{m.custom && m.role !== "admin" && m.role !== "owner" && <span className="ms-1 text-[9px] text-amber-700">({t("staff.customTag")})</span>}</div>
                        {m.role !== "admin" && m.role !== "owner" && <div className="mt-0.5 flex flex-wrap gap-1">{m.effective.map((p) => <span key={p} className="rounded bg-gray-100 px-1 py-0.5 text-[9px] font-bold text-gray-600">{t(`staff.perm.${p}.title`)}</span>)}</div>}
                      </td>
                      <td className="px-2 py-2.5 text-[11px] text-gray-500">{m.role === "admin" || m.role === "owner" ? t("staff.allBranchesShort") : m.branches.map((b) => branchName.get(b.branchId) ?? "—").join("، ")}</td>
                      <td className="px-2 py-2.5 text-center">{m.active ? <Pill tone="green">{t("staff.on")}</Pill> : <Pill tone="gray">{t("staff.off")}</Pill>}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1">
                          <button type="button" className="btn-ghost !px-2 !py-1 text-[11px]" disabled={locked} onClick={() => setEdit({ member: m })}>✏️ {t("staff.edit")}</button>
                          <button type="button" className="btn-ghost !px-2 !py-1 text-[11px]" disabled={locked} onClick={() => setPinFor(m)}>🔑 {t("staff.resetPin")}</button>
                          {!mine && <button type="button" className="btn-ghost !px-2 !py-1 text-[11px]" disabled={locked} onClick={() => void toggleActive(m)}>{m.active ? t("staff.disable") : t("staff.enable")}</button>}
                          {!mine && <button type="button" className="btn-ghost !px-2 !py-1 text-[11px] text-red-600" disabled={locked} aria-label={t("staff.delete")} onClick={() => void remove(m)}>🗑</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] font-semibold text-gray-500">{t("staff.liveNote")}</p>
      </section>
      {edit && data && <StaffDialog member={edit.member} data={data} onClose={() => setEdit(null)} />}
      {pinFor && <ResetPin member={pinFor} onClose={() => setPinFor(null)} />}
    </div>
  );
}
